import { Contact } from '../../../components/address-book';
import { Address, AddressResponseData, EnvelopePreviewBody, RequestBody } from '../../../global-types';
import { ProjectTypeCode } from '../../../global-types/initialization-data';
import { getMonolithUrl } from '../../../utils';
import { getDisplayName } from '../../../utils/address/get-display-name';

type Params = {
  productId: string | null;
  projectId: string;
  address: AddressResponseData | Address | null;
  scanCode: string;
  coverPreviewUrl: string;
  lineItemUUID: string | null;
  quantity: string;
};

const monolithUrl = getMonolithUrl();

/**
 *
 * @param param Project information used for the add to cart request payload
 * @returns the request body to be used in add to cart api POST request
 */
export const getAddToCartRequestBody = ({
  productId,
  projectId,
  address,
  scanCode,
  coverPreviewUrl,
  lineItemUUID,
  quantity,
}: Params): RequestBody => {
  const requestBody: RequestBody = {
    pid: productId || '',
    quantity,
    projectID: projectId || '',
    fulfillmentProductTypeCode: 'S', // Sign and Send --- probably returns an error at the moment
    shippingOptionCode: 'S', // Only S - Standard for now
    scanCode: scanCode || '',
    thumbnailImageURL: coverPreviewUrl,
  };
  if (address) {
    const addressBody = {
      firstName: address.first_name,
      lastName: address.last_name,
      displayName: address.display_name || '',
      companyName: address.company_name,
      addressLine1: address.address_line_1,
      addressLine2: address.address_line_2,
      city: address.city,
      stateCode: address.state_code,
      zip: address.zip,
      countryCode: 'US',
    };
    Object.assign(requestBody, addressBody);
  }
  if (lineItemUUID) {
    requestBody.lineItemUUID = lineItemUUID;
  }
  return requestBody;
};

export const handleCloseAndKeepShopping = (projectTypeCode: ProjectTypeCode) => {
  const sAndSQuery = process.env.REACT_APP_SAS_CARDS_PATH;
  const podCardsPath = process.env.REACT_APP_CARDS_PATH;
  const exitUrl = projectTypeCode !== 'S' ? `${monolithUrl}/${podCardsPath}` : `${monolithUrl}/cards${sAndSQuery}`;
  window.location.href = exitUrl;
};

/** Whether the raw (unresolved) display_name field was set on the given address */
const isDisplayNamePopulated = (address?: AddressResponseData | Address | Contact | null): boolean =>
  !!address?.display_name?.trim();

/**
 * @param address Address to be included in envelope
 * @returns Object containing just the necessary address keys for the envelope preview body
 */
const getEnvelopeAddress = (address: AddressResponseData | Address | Contact) => {
  const { first_name, last_name, display_name, company_name, address_line_1, address_line_2, city, state_code, zip } =
    address;
  return {
    first_name,
    last_name,
    display_name: getDisplayName(first_name, last_name, display_name),
    company_name,
    address_line_1,
    address_line_2,
    city,
    state_code,
    zip,
  };
};

export const getEnvelopePreviewBody = ({
  color,
  envelopeSize,
  recipient,
  sender,
}: {
  color: string;
  envelopeSize: string;
  recipient?: AddressResponseData | Address | Contact | null;
  sender?: AddressResponseData | Address | Contact | null;
}): string => {
  const reqBody: EnvelopePreviewBody = {
    envelope_size: envelopeSize,
    color,
    use_display_name_for_recipient: false,
    use_display_name_for_sender: false,
  };
  if (recipient) {
    reqBody.recipient_address = getEnvelopeAddress(recipient);
    reqBody.use_display_name_for_recipient = isDisplayNamePopulated(recipient);
  }
  if (sender) {
    reqBody.sender_address = getEnvelopeAddress(sender);
    reqBody.use_display_name_for_sender = isDisplayNamePopulated(sender);
  }
  return JSON.stringify(reqBody);
};

export { getOrderRequestBody } from './get-order-request-body';
export { finalizeOrder } from './finalize-order';
export { handleDigitalOrdersError } from './handle-digital-orders-error';
